import { JUGUANG_MCP_SERVER_NAME } from "./enterprise-credentials.js";
import { pluginNamespacedMcpServerName } from "./mcp-plugin-name.js";
/**
 * 聚光写路径 commit 工具清单 — 无人值守硬闸的名字单源（红线 #9）。
 *
 * 产品契约（plugins/dosia-brand-marketing/skills/juguang-capability/references/
 * write-ops.md）：每个 commit 都改变真实投放、必须有真实用户确认，「无人值守
 * cron/loop 一律不 commit」——四个 commit 工具语义同类，无人值守面必须同禁。
 *
 * 为什么精确枚举而不是 `juguang_*_commit` shape matcher：SDK `disallowedTools`
 * 只承诺精确工具名 + **server 级**通配（`mcp__server` / `mcp__server__*` /
 * `mcp__*`，sdk.d.ts 0.3.227 AgentDefinition.disallowedTools 注），Pi 腿同档
 * （`compileToolPatterns`，packages/core session-tool-surface.ts：精确名 + server 级
 * 模式按工具归属判定，2026-08-25 PR-1c 起；此前是纯精确名 Set）；工具名**中段**通配
 * 在两条腿上都无契约语义，钱闸不押未承诺行为——若它不匹配就是静默假闸。
 * 本清单的两种全限定名在 Pi 腿仍走精确名分支命中（external MCP 的 wire 名就是这一串），
 * 不依赖 PR-1c 的归属判定。shape 锁改放测试面：本文件的 .test.ts 用
 * `/^juguang_[a-z0-9_]+_commit$/` 与插件声明面 skill-declared-tools.json 双向
 * 对账，插件新增 commit 工具而漏进本清单时测试转红。
 */
export const JUGUANG_COMMIT_TOOL_LEAVES = [
    "juguang_campaign_status_commit",
    "juguang_unit_status_commit",
    "juguang_creativity_status_commit",
    "juguang_unit_bid_commit",
];
/**
 * Fully-qualified commit tool names in BOTH mount shapes:
 *   - cloud agent-host materializes plugin connectors under the bare server
 *     name → `mcp__catalyst-juguang__<leaf>`;
 *   - desktop mounts plugin `.mcp.json` connectors under the plugin-namespaced
 *     name → `mcp__plugin_catalyst-juguang_catalyst-juguang__<leaf>`.
 * Denying a name that is not mounted is a no-op, so consumers include the whole
 * list unconditionally (see packages/core session-tool-surface.ts).
 */
export const JUGUANG_COMMIT_TOOL_NAMES = juguangToolFullNames(JUGUANG_COMMIT_TOOL_LEAVES);
/**
 * 盯盘写类工具清单（N-57 期 4 D4-03）——无人值守一律不给（产品拍板 2026-09-28：建 / 改 / 删
 * 盯盘规则、标「已处理」、登记数据需求都要员工本人确认；提供方
 * `online-projects:docs/design/auto-monitor.md`「建规则指引」同样写明员工明确确认后才保存）。
 * 无人值守（云端 agentTurn，含盯盘巡检与每周盯盘周报；桌面 cron / loop）无法取得确认，
 * 所以与 commit 清单并列进 `buildUnattendedDisallowedTools` 的**无条件基线**。
 *
 * 为什么精确枚举：理由同 {@link JUGUANG_COMMIT_TOOL_LEAVES}（禁用表只承诺精确名 + server 级通配）。
 * 完备性锁在测试面：`skill-declared-tools.json` 里每个 `juguang_monitor_*` 名要么在只读名单、
 * 要么在本清单，新增盯盘写工具而漏进这里时测试转红。
 */
export const JUGUANG_MONITOR_MUTATION_TOOL_LEAVES = [
    "juguang_monitor_rule_create",
    "juguang_monitor_rule_update",
    "juguang_monitor_rule_delete",
    "juguang_monitor_event_ack",
    "juguang_monitor_data_request",
];
/** 盯盘写类工具在两种挂载形态下的全名（同 {@link JUGUANG_COMMIT_TOOL_NAMES}）。 */
export const JUGUANG_MONITOR_MUTATION_TOOL_NAMES = juguangToolFullNames(JUGUANG_MONITOR_MUTATION_TOOL_LEAVES);
/**
 * 盯盘巡检 agent（N-57 期 4 D4-03，产品拍板 2026-09-28「收窄到只读聚光工具」）。巡检载体任务以
 * `execution.agent` + `agentPolicy: "required"` 指定它；它的 `tools` 白名单就是巡检的**全部**工具面——
 * 没有任何内置工具（Bash / Write / WebFetch …），只有下面九个只读聚光工具的两种挂载全名。员工写的
 * 巡检要求原样进提示词、且不经审批，所以工具面收窄是这一路的唯一防线（名字解析不到时
 * `required` 让这次执行在第一次模型请求前失败，巡检降级为数字卡片，不会退回全工具面）。
 *
 * 定义文件：`plugins/catalyst-juguang/agents/juguang-monitor-inspector.md`（插件 agent，随插件可见性
 * 与分发；Claude 腿由 SDK 原生 `Options.agent` 解析并把白名单施加到主线程，Pi 腿由 core
 * `applyResolvedMainAgentToolPolicy` 施加）。白名单与本常量逐一相等由 core 测试钉住。
 */
export const JUGUANG_MONITOR_INSPECTOR_AGENT = "catalyst-juguang:juguang-monitor-inspector";
/** 盯盘巡检 agent 的只读工具叶名：D1 项目数据六个 + 盯盘三个只读查询。 */
export const JUGUANG_MONITOR_INSPECTOR_TOOL_LEAVES = [
    "juguang_project_list",
    "juguang_project_delivery_overview",
    "juguang_project_data_health",
    "juguang_project_report",
    "juguang_project_trend",
    "juguang_project_notes",
    "juguang_monitor_event_list",
    "juguang_monitor_event_get",
    "juguang_monitor_rule_list",
];
/** 盯盘巡检 agent 的工具白名单全名（两种挂载形态）。 */
export const JUGUANG_MONITOR_INSPECTOR_TOOL_NAMES = juguangToolFullNames(JUGUANG_MONITOR_INSPECTOR_TOOL_LEAVES);
/** 叶名 → 两种挂载形态的全名（云端裸 server 名 / 桌面插件命名空间名）。 */
function juguangToolFullNames(leaves) {
    const pluginServer = pluginNamespacedMcpServerName(JUGUANG_MCP_SERVER_NAME, JUGUANG_MCP_SERVER_NAME);
    return leaves.flatMap((leaf) => [
        `mcp__${JUGUANG_MCP_SERVER_NAME}__${leaf}`,
        `mcp__${pluginServer}__${leaf}`,
    ]);
}
// ===================== 写操作审批（N-203 第 3 期） =====================
//
// 员工在 DOSIA 里确认后，由企业后端签一张 RS256 回执，宿主把它注入 commit；后台（media-backend
// `juguangApprovalReceipt.js`）只持公钥验签、按 jti 一次性消费、与 preview 一一配对。产品经理 2026-11-03 定：
// 确认只认员工已登录的 DOSIA 账号，不要设备层（dosia#2065 的设备 Ed25519 登记不移植）；只放行计划、单元、
// 创意三个启停 commit，改出价不在审批面（仍只在无人值守禁单里）。
/** 预览工具叶名：commit 必须先有同类 preview（`expectedJuguangCommitTool` 一一对应）。 */
export const JUGUANG_PREVIEW_TOOL_LEAVES = [
    "juguang_campaign_status_preview",
    "juguang_unit_status_preview",
    "juguang_creativity_status_preview",
    "juguang_unit_bid_preview",
];
/** 审批能放行的 commit：只有三个启停。改出价 `juguang_unit_bid_commit` 不在这里，宿主见到一律拒。 */
export const JUGUANG_APPROVABLE_COMMIT_TOOL_LEAVES = [
    "juguang_campaign_status_commit",
    "juguang_unit_status_commit",
    "juguang_creativity_status_commit",
];
const COMMIT_LEAF_SET = new Set(JUGUANG_COMMIT_TOOL_LEAVES);
const PREVIEW_LEAF_SET = new Set(JUGUANG_PREVIEW_TOOL_LEAVES);
const APPROVABLE_LEAF_SET = new Set(JUGUANG_APPROVABLE_COMMIT_TOOL_LEAVES);
const JUGUANG_SERVER_SEGMENTS = new Set([
    JUGUANG_MCP_SERVER_NAME,
    pluginNamespacedMcpServerName(JUGUANG_MCP_SERVER_NAME, JUGUANG_MCP_SERVER_NAME),
]);
/**
 * 只认两种挂载全名（云端裸 server 名 / 桌面插件命名空间名）下的聚光 preview / commit 叶名；
 * 别名、仿冒 server、未知叶名一律 null（调用方据此 fail closed）。
 */
export function matchJuguangApprovalTool(toolName) {
    if (typeof toolName !== "string" || !toolName.startsWith("mcp__"))
        return null;
    const splitAt = toolName.lastIndexOf("__");
    if (splitAt <= "mcp__".length || splitAt >= toolName.length - 2)
        return null;
    const server = toolName.slice("mcp__".length, splitAt);
    const leaf = toolName.slice(splitAt + 2);
    if (!JUGUANG_SERVER_SEGMENTS.has(server))
        return null;
    if (COMMIT_LEAF_SET.has(leaf))
        return { fullName: toolName, leaf: leaf, kind: "commit" };
    if (PREVIEW_LEAF_SET.has(leaf))
        return { fullName: toolName, leaf: leaf, kind: "preview" };
    return null;
}
/** 这个 commit 叶名能不能走审批（只有三个启停）。 */
export function isApprovableJuguangCommit(leaf) {
    return APPROVABLE_LEAF_SET.has(leaf);
}
/** preview 对应的 commit（`*_preview` → `*_commit`）。 */
export function expectedJuguangCommitTool(previewLeaf) {
    return previewLeaf.replace(/_preview$/, "_commit");
}
const POTENTIAL_COMMIT_LEAF_RE = /^juguang_[a-z0-9_]+_commit(?:_\d+)?$/i;
/**
 * 工具名**长得像**聚光 commit（任何 server 段、含 pi 腿 wire 名去重后缀 `_2`）。宿主拿它先把这类调用全部
 * 截进审批面，再用 {@link matchJuguangApprovalTool} 精确认：认不出的（仿冒 server、未知叶名、去重改名）一律拒，
 * 绝不落回通用权限判定或自动执行档的放行。
 */
export function isPotentialJuguangCommitToolName(toolName) {
    if (typeof toolName !== "string" || toolName.length === 0)
        return false;
    let leaf = toolName;
    if (toolName.startsWith("mcp__")) {
        const rest = toolName.slice("mcp__".length);
        const splitAt = rest.lastIndexOf("__");
        leaf = splitAt >= 0 ? rest.slice(splitAt + 2) : rest;
    }
    return POTENTIAL_COMMIT_LEAF_RE.test(leaf);
}
const SUMMARY_ID_MAX = 256;
const SUMMARY_DESCRIPTION_MAX = 2_000;
const SUMMARY_ADVERTISERS_MAX = 500;
const RECEIPT_MAX_CHARS = 8_192;
const boundedText = (value, max) => typeof value === "string" && value.trim().length > 0 && value.length <= max;
const isAdvertiserId = (value) => (typeof value === "number" && Number.isSafeInteger(value) && value > 0)
    || (typeof value === "string" && /^[1-9]\d{0,19}$/.test(value));
/**
 * 企业后端 inspect 摘要的运行时校验（跨进程 / 跨 IPC 的值不是类型证明）。形状不对 → null，调用方据此拒绝
 * （宿主）或不渲染摘要（界面）。只拷贝契约里的字段。
 */
export function parseJuguangApprovalTrustedSummary(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return null;
    const v = value;
    if (!boundedText(v.previewId, SUMMARY_ID_MAX)
        || !boundedText(v.previewDigest, SUMMARY_ID_MAX)
        || !boundedText(v.operation, SUMMARY_ID_MAX)
        || !boundedText(v.description, SUMMARY_DESCRIPTION_MAX)
        || !boundedText(v.expiresAt, SUMMARY_ID_MAX)
        || !Number.isFinite(Date.parse(v.expiresAt))
        || typeof v.expectedCommitTool !== "string"
        || !isApprovableJuguangCommit(v.expectedCommitTool)
        || !Number.isSafeInteger(v.candidateCount)
        || v.candidateCount <= 0
        || !Array.isArray(v.advertiserIds)
        || v.advertiserIds.length === 0
        || v.advertiserIds.length > SUMMARY_ADVERTISERS_MAX
        || !v.advertiserIds.every(isAdvertiserId))
        return null;
    const advertiserIds = v.advertiserIds.filter(isAdvertiserId);
    return {
        previewId: v.previewId,
        previewDigest: v.previewDigest,
        operation: v.operation,
        advertiserIds,
        candidateCount: v.candidateCount,
        expiresAt: v.expiresAt,
        expectedCommitTool: v.expectedCommitTool,
        description: v.description,
    };
}
/** 企业后端签回执响应的运行时校验：回执必须是三段 base64url 的 JWT 形状、有效期可解析。不对 → null。 */
export function parseJuguangApprovalReceiptResponse(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return null;
    const v = value;
    if (!boundedText(v.approvalReceipt, RECEIPT_MAX_CHARS)
        || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(v.approvalReceipt)
        || !boundedText(v.expiresAt, SUMMARY_ID_MAX)
        || !Number.isFinite(Date.parse(v.expiresAt)))
        return null;
    return { approvalReceipt: v.approvalReceipt, expiresAt: v.expiresAt };
}
//# sourceMappingURL=juguang-approval-contract.js.map